/**
 * Tiny dense linear solvers for the small systems (2x2 .. 6x6) produced by
 * planar position analysis. Two variants:
 *
 *  - {@link solveDenseNumeric} — plain floats, used inside the Newton
 *    convergence loop where speed matters and gradients are not needed.
 *  - {@link solveDenseValue} — the same Gauss-Jordan elimination but over
 *    {@link Value}s, so a single differentiable Newton step propagates
 *    d(solution)/d(parameters) exactly (the implicit-function-theorem trick).
 *
 * Both use partial pivoting; pivot *selection* is based on magnitude and is
 * piecewise constant, so it does not disturb differentiability away from ties.
 */

import { Value, V } from "../autodiff/value.js";

export class SingularSystemError extends Error {
  constructor(message = "linear system is singular or ill-conditioned") {
    super(message);
    this.name = "SingularSystemError";
  }
}

/** Solve `A x = b` for x. `A` is row-major n x n; `b` has length n. */
export function solveDenseNumeric(A: number[][], b: number[]): number[] {
  const n = b.length;
  // Augmented matrix, copied so callers keep their inputs.
  const M: number[][] = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    let pivot = col;
    let best = Math.abs(M[col][col]);
    for (let r = col + 1; r < n; r++) {
      const mag = Math.abs(M[r][col]);
      if (mag > best) {
        best = mag;
        pivot = r;
      }
    }
    if (best < 1e-13) throw new SingularSystemError();
    if (pivot !== col) {
      const tmp = M[col];
      M[col] = M[pivot];
      M[pivot] = tmp;
    }
    const pv = M[col][col];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const factor = M[r][col] / pv;
      if (factor === 0) continue;
      for (let c = col; c <= n; c++) M[r][c] -= factor * M[col][c];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

/** Solve `A x = b` over Values, keeping the computation graph intact. */
export function solveDenseValue(A: Value[][], b: Value[]): Value[] {
  const n = b.length;
  const M: Value[][] = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    let pivot = col;
    let best = Math.abs(M[col][col].data);
    for (let r = col + 1; r < n; r++) {
      const mag = Math.abs(M[r][col].data);
      if (mag > best) {
        best = mag;
        pivot = r;
      }
    }
    if (best < 1e-13) throw new SingularSystemError();
    if (pivot !== col) {
      const tmp = M[col];
      M[col] = M[pivot];
      M[pivot] = tmp;
    }
    const pv = M[col][col];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      if (M[r][col].data === 0) continue;
      const factor = M[r][col].div(pv);
      for (let c = col; c <= n; c++) {
        M[r][c] = M[r][c].sub(factor.mul(M[col][c]));
      }
    }
  }
  return M.map((row, i) => row[n].div(row[i]));
}

/** Build an n x n matrix of zero Values. */
export function zerosValue(rows: number, cols: number): Value[][] {
  return Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => V(0)),
  );
}
